// frontend/src/components/WorkPackageComposer.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-10-07
// Updated:      2026-10-20 08:27 BST
//
// Description:
//   Shared, theme-aware and mobile-friendly Work Package selector/composer used
//   by JobFormWidget, GlobalJobCreateFlyout and JobEditModal. Resolves the
//   published workflow binding against the complete independent Work Package
//   taxonomy path before a package is accepted. Resource is nullable.
//
// Change Log:
//   2026-10-20 08:27 BST: Restored per-Work-Package location selector and map-pin creation;
//                     location choices use tenant-scoped Workflow API.
//   2026-10-08 12:14 BST: Corrected binding preview to use Type -> Subtype ->
//                         Subtype2 and send the complete selected taxonomy path
//                         to /workflow-bindings/resolve-work-package. Replaced
//                         fixed-width package row with contained stacked layout
//                         so JobEditModal cannot be widened by this component.
//   2026-10-07 15:42 BST: Initial shared composer. Makes package selection explicit,
//                         prioritises ATC package types for ATC jobs, and supports
//                         controlled drafts.

import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import LocationEditModal from "./LocationEditModal.jsx";

const resourceName = (r) => r?.name || [r?.first_name, r?.last_name].filter(Boolean).join(" ") || r?.email || `Resource ${r?.id}`;

export default function WorkPackageComposer({ jobTypeName = "", resources = [], value = [], onChange, disabled = false }) {
  const [tree, setTree] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationError, setLocationError] = useState("");
  const [creatingLocationFor, setCreatingLocationFor] = useState(null);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedSubtypeId, setSelectedSubtypeId] = useState("");
  const [selectedSubtype2Id, setSelectedSubtype2Id] = useState("");
  const [bindingPreview, setBindingPreview] = useState(null);
  const [bindingMessage, setBindingMessage] = useState("");
  const [bindingLoading, setBindingLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError("");
    axiosInstance.get("/work-package-taxonomy/tree", { baseURL: "/api" })
      .then(({ data }) => {
        if (live) setTree(Array.isArray(data) ? data : (data?.items || []));
      })
      .catch((e) => {
        if (live) setError(e?.response?.data?.detail || "Unable to load Work Package taxonomy.");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let live = true;
    axiosInstance.get("/work-package-locations", { baseURL: "/api" })
      .then(({ data }) => { if (live) setLocations(Array.isArray(data) ? data : []); })
      .catch(() => { if (live) setLocationError("Unable to load Work Package locations."); });
    return () => { live = false; };
  }, []);

  const handleLocationCreated = (location) => {
    if (!location?.id || !creatingLocationFor) return;
    setLocations((rows) => [location, ...rows.filter((row) => Number(row.id) !== Number(location.id))]);
    update(creatingLocationFor, { location_id: String(location.id) });
    setCreatingLocationFor(null);
    setLocationError("");
  };

  const sortedTypes = useMemo(() => {
    const atcJob = String(jobTypeName || "").toUpperCase().includes("ATC");
    return [...tree].sort((a, b) => {
      const isAtc = (x) => `${x?.name || ""} ${x?.short_code || ""}`.toUpperCase().includes("ATC");
      if (atcJob && isAtc(a) !== isAtc(b)) return isAtc(a) ? -1 : 1;
      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
  }, [tree, jobTypeName]);

  const selectedType = useMemo(
    () => tree.find((x) => String(x.id) === String(selectedTypeId)) || null,
    [tree, selectedTypeId]
  );

  const subtypes = useMemo(
    () => Array.isArray(selectedType?.subtypes) ? selectedType.subtypes : [],
    [selectedType]
  );

  const selectedSubtype = useMemo(
    () => subtypes.find((x) => String(x.id) === String(selectedSubtypeId)) || null,
    [subtypes, selectedSubtypeId]
  );

  const subtypes2 = useMemo(
    () => Array.isArray(selectedSubtype?.subtypes2) ? selectedSubtype.subtypes2 : [],
    [selectedSubtype]
  );

  const selectedSubtype2 = useMemo(
    () => subtypes2.find((x) => String(x.id) === String(selectedSubtype2Id)) || null,
    [subtypes2, selectedSubtype2Id]
  );

  useEffect(() => {
    let live = true;
    setBindingPreview(null);
    setBindingMessage("");

    if (!selectedTypeId) return () => { live = false; };

    // If the selected Type has subtypes, wait for the user to choose one. This
    // prevents a false Type-level "not bound" message when the binding is
    // intentionally defined at Subtype or Subtype2 scope.
    if (subtypes.length > 0 && !selectedSubtypeId) {
      setBindingMessage("Select a Work Package Subtype to resolve the published workflow.");
      return () => { live = false; };
    }

    // Likewise, where Subtype2 choices exist, resolve only after the complete
    // taxonomy path has been selected so the most-specific binding can win.
    if (subtypes2.length > 0 && !selectedSubtype2Id) {
      setBindingMessage("Select Work Package Subtype 2 to resolve the published workflow.");
      return () => { live = false; };
    }

    const params = {
      work_package_type_id: Number(selectedTypeId),
    };
    if (selectedSubtypeId) params.work_package_subtype_id = Number(selectedSubtypeId);
    if (selectedSubtype2Id) params.work_package_subtype2_id = Number(selectedSubtype2Id);

    setBindingLoading(true);
    setBindingMessage("Resolving published workflow…");
    axiosInstance.get("/workflow-bindings/resolve-work-package", { baseURL: "/api", params })
      .then(({ data }) => {
        if (!live) return;
        if (data?.binding_found) {
          setBindingPreview(data);
          setBindingMessage("");
        } else {
          setBindingPreview(null);
          setBindingMessage(data?.resolution_reason || "No published workflow binding found for this Work Package taxonomy.");
        }
      })
      .catch((e) => {
        if (!live) return;
        setBindingPreview(null);
        setBindingMessage(e?.response?.data?.detail || "No published workflow binding found for this Work Package taxonomy.");
      })
      .finally(() => {
        if (live) setBindingLoading(false);
      });

    return () => { live = false; };
  }, [selectedTypeId, selectedSubtypeId, selectedSubtype2Id, subtypes.length, subtypes2.length]);

  const handleTypeChange = (nextId) => {
    setSelectedTypeId(nextId);
    setSelectedSubtypeId("");
    setSelectedSubtype2Id("");
    setBindingPreview(null);
    setBindingMessage("");
  };

  const handleSubtypeChange = (nextId) => {
    setSelectedSubtypeId(nextId);
    setSelectedSubtype2Id("");
    setBindingPreview(null);
    setBindingMessage("");
  };

  const addSelected = () => {
    if (!selectedType || !bindingPreview?.binding_found) return;

    const taxonomyTitle = [selectedType?.name, selectedSubtype?.name, selectedSubtype2?.name]
      .filter(Boolean)
      .join(" — ");

    const draft = {
      key: `${Date.now()}-${selectedType.id}-${selectedSubtype?.id || 0}-${selectedSubtype2?.id || 0}`,
      title: taxonomyTitle || selectedType.name || "Work Package",
      package_type: selectedType.short_code || "work_package",
      work_package_type_id: Number(selectedType.id),
      work_package_subtype_id: selectedSubtype ? Number(selectedSubtype.id) : "",
      work_package_subtype2_id: selectedSubtype2 ? Number(selectedSubtype2.id) : "",
      assigned_resource_id: "",
      location_id: "",
      binding: bindingPreview,
      binding_error: "",
    };

    onChange?.([...(value || []), draft]);
    setSelectedTypeId("");
    setSelectedSubtypeId("");
    setSelectedSubtype2Id("");
    setBindingPreview(null);
    setBindingMessage("");
  };

  const update = (key, patch) => onChange?.((value || []).map((row) => row.key === key ? { ...row, ...patch } : row));
  const remove = (key) => onChange?.((value || []).filter((row) => row.key !== key));

  return (
    <section className="w-full min-w-0 max-w-full overflow-hidden rounded-xl border border-gray-300 dark:border-gray-600 p-3 sm:p-4 bg-white/60 dark:bg-gray-900/30">
      <div className="font-bold text-base mb-1">Select Work Packages</div>
      <div className="text-sm opacity-70 mb-3">Choose the Work Package taxonomy, confirm the published workflow match, then add it to this Job.</div>

      <div className="w-full min-w-0 grid grid-cols-1 gap-3">
        <div className="min-w-0">
          <label className="block text-xs font-semibold mb-1">Work Package Type</label>
          <select
            className="block w-full min-w-0 max-w-full min-h-11 border rounded-lg px-3 bg-white dark:bg-gray-900 dark:border-gray-600"
            value={selectedTypeId}
            onChange={(e) => handleTypeChange(e.target.value)}
            disabled={disabled || loading}
          >
            <option value="">{loading ? "Loading Work Package types…" : "-- Select Work Package Type --"}</option>
            {sortedTypes.map((x) => (
              <option key={x.id} value={x.id}>
                {String(jobTypeName).toUpperCase().includes("ATC") && `${x.name || ""} ${x.short_code || ""}`.toUpperCase().includes("ATC") ? `★ ${x.name}` : x.name}
              </option>
            ))}
          </select>
        </div>

        {!!selectedTypeId && subtypes.length > 0 && (
          <div className="min-w-0">
            <label className="block text-xs font-semibold mb-1">Work Package Subtype</label>
            <select
              className="block w-full min-w-0 max-w-full min-h-11 border rounded-lg px-3 bg-white dark:bg-gray-900 dark:border-gray-600"
              value={selectedSubtypeId}
              onChange={(e) => handleSubtypeChange(e.target.value)}
              disabled={disabled}
            >
              <option value="">-- Select Work Package Subtype --</option>
              {subtypes.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
        )}

        {!!selectedSubtypeId && subtypes2.length > 0 && (
          <div className="min-w-0">
            <label className="block text-xs font-semibold mb-1">Work Package Subtype 2</label>
            <select
              className="block w-full min-w-0 max-w-full min-h-11 border rounded-lg px-3 bg-white dark:bg-gray-900 dark:border-gray-600"
              value={selectedSubtype2Id}
              onChange={(e) => setSelectedSubtype2Id(e.target.value)}
              disabled={disabled}
            >
              <option value="">-- Select Work Package Subtype 2 --</option>
              {subtypes2.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
        )}

        {!!selectedTypeId && (
          <div className="min-w-0 rounded-lg border border-gray-300 dark:border-gray-600 p-3 text-sm">
            {bindingLoading ? (
              <span className="opacity-70">Resolving published workflow…</span>
            ) : bindingPreview?.binding_found ? (
              <span className="text-emerald-700 dark:text-emerald-400">
                Workflow matched · binding {bindingPreview.binding_id} · published version {bindingPreview.workflow_template_version_id} · {bindingPreview.binding_scope}
              </span>
            ) : (
              <span className="text-amber-700 dark:text-amber-400">{bindingMessage || "Select the Work Package taxonomy to resolve its workflow."}</span>
            )}
          </div>
        )}

        <button
          type="button"
          className="w-full sm:w-auto sm:justify-self-start min-h-11 px-4 rounded-lg bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white font-semibold disabled:opacity-50"
          disabled={disabled || bindingLoading || !bindingPreview?.binding_found}
          onClick={addSelected}
        >
          Add Work Package
        </button>
      </div>

      {error && <div className="text-sm text-red-700 mt-2">{error}</div>}

      {locationError && <div role="alert" className="text-sm text-red-700 dark:text-red-300 mt-2">{locationError}</div>}
      <LocationEditModal
        open={Boolean(creatingLocationFor)}
        onClose={() => setCreatingLocationFor(null)}
        onCreated={handleLocationCreated}
        initial={{}}
      />

      <div className="w-full min-w-0 mt-4 space-y-3">
        {(value || []).map((draft) => (
          <article key={draft.key} className="w-full min-w-0 max-w-full overflow-hidden rounded-xl border border-gray-300 dark:border-gray-600 p-3">
            <div className="w-full min-w-0 grid grid-cols-1 gap-3">
              <div className="min-w-0">
                <label className="block text-xs font-semibold mb-1">Package title</label>
                <input
                  className="block w-full min-w-0 max-w-full min-h-11 border rounded-lg px-3 bg-white dark:bg-gray-900 dark:border-gray-600"
                  value={draft.title || ""}
                  onChange={(e) => update(draft.key, { title: e.target.value })}
                />
              </div>

              <div className="min-w-0">
                <label className="block text-xs font-semibold mb-1">Resource (optional)</label>
                <select
                  className="block w-full min-w-0 max-w-full min-h-11 border rounded-lg px-3 bg-white dark:bg-gray-900 dark:border-gray-600"
                  value={draft.assigned_resource_id || ""}
                  onChange={(e) => update(draft.key, { assigned_resource_id: e.target.value })}
                >
                  <option value="">Unassigned</option>
                  {(resources || []).map((r) => <option key={r.id} value={r.id}>{resourceName(r)}</option>)}
                </select>
              </div>

              <div className="min-w-0">
                <label className="block text-xs font-semibold mb-1">Work Package location (optional)</label>
                <div className="flex flex-col sm:flex-row gap-2 min-w-0">
                  <select
                    className="block w-full min-w-0 flex-1 min-h-11 border rounded-lg px-3 bg-white dark:bg-gray-900 dark:border-gray-600"
                    value={draft.location_id || ""}
                    onChange={(e) => update(draft.key, { location_id: e.target.value })}
                    disabled={disabled}
                  >
                    <option value="">No Work Package location</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={String(loc.id)}>
                        {loc.name || `Location ${loc.id}`}{loc.postcode ? ` — ${loc.postcode}` : ""}
                      </option>
                    ))}
                  </select>
                  <button type="button" disabled={disabled}
                    className="min-h-11 px-3 rounded-lg border border-gray-400 dark:border-gray-600 font-semibold"
                    onClick={() => setCreatingLocationFor(draft.key)}>
                    Create location / map pin
                  </button>
                </div>
              </div>

              <div className="min-w-0 text-xs break-words">
                {draft.binding ? (
                  <span className="text-emerald-700 dark:text-emerald-400">
                    Workflow matched · binding {draft.binding.binding_id} · published version {draft.binding.workflow_template_version_id} · {draft.binding.binding_scope}
                  </span>
                ) : (
                  <span className="text-amber-700 dark:text-amber-400">{draft.binding_error || "Workflow binding required."}</span>
                )}
              </div>

              <button
                type="button"
                className="w-full sm:w-auto sm:justify-self-start min-h-11 px-3 rounded-lg border border-red-400 text-red-700 dark:text-red-300"
                onClick={() => remove(draft.key)}
              >
                Remove
              </button>
            </div>
          </article>
        ))}

        {!(value || []).length && (
          <div className="w-full min-w-0 rounded-lg border border-dashed p-4 text-sm opacity-65 text-center">
            No Work Packages selected. Use the taxonomy selectors above to add one.
          </div>
        )}
      </div>
    </section>
  );
}
